import type { Artifact } from "@/services/projectsApi";

/** A table is stored as CSV, and that is the only thing that distinguishes it
 *  from a picture once extraction has run — the register's type is about what
 *  the artifact is in the workbook, not what we hold for it.
 *
 *  Shared because getting this wrong is silent: a table misread as a picture
 *  renders in an <img> that never loads, and a picture misread as a table is
 *  parsed as CSV into one meaningless row. */
export const isTableArtifact = (artifact: Pick<Artifact, "contentType"> | null | undefined) =>
  artifact?.contentType === "text/csv";

/** Whether enrichment still owns this artifact's description.
 *
 *  While it does, the worker will overwrite whatever is on the row when it
 *  finishes, so the UI must not offer an edit and the API answers 409 to one.
 *  Both sides read this same pair of states. */
export const isArtifactEnriching = (artifact: Pick<Artifact, "status"> | null | undefined) =>
  artifact?.status === "Queued" || artifact?.status === "Processing";

/** The artifacts a section has selected as sources, in project order. Sources
 *  store the register uid; only active, Ready artifacts are usable — anything
 *  else hasn't been indexed and has no settled content to show. */
export const selectSectionArtifacts = (
  artifactIds: string[] | null | undefined,
  artifacts: Artifact[] | null | undefined
): Artifact[] => {
  const selected = new Set(artifactIds ?? []);
  if (selected.size === 0) return [];
  return (artifacts ?? []).filter((a) => selected.has(a.uid) && a.isActive && a.status === "Ready");
};

/** The shape anything needs to be grouped under its sheet. Loose enough to
 *  cover both the upload preview's register rows and persisted artifacts. */
export interface SheetGroupable {
  uid: string;
  name: string;
  type: string;
  section?: string | null;
  sheetCodeName?: string | null;
  tabNumber?: number | null;
}

export interface SheetGroup<T extends SheetGroupable> {
  key: string;
  label: string;
  tabNumber: number;
  leaves: T[];
}

const UNGROUPED_SECTION = "Artefacts";

export const isSheetRow = (item: Pick<SheetGroupable, "type">) =>
  (item.type ?? "").trim().toLowerCase() === "sheet";

/** Groups artefacts by the sheet they came from, in workbook order.
 *
 *  A group is keyed by whatever identifies its sheet, in order of reliability:
 *  the VBA code name, else the visible tab name, else a catch-all. Sheet-type
 *  rows only supply a group's label and position — they never create one.
 *  Persisted artifacts never include Sheet rows at all, since those aren't
 *  selectable and so are never stored, so grouping has to work from the leaves
 *  alone. */
export const groupArtifactsBySheet = <T extends SheetGroupable>(items: T[]): SheetGroup<T>[] => {
  const groups = new Map<string, SheetGroup<T>>();

  const groupFor = (item: T) => {
    const key = item.sheetCodeName || item.section || UNGROUPED_SECTION;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: item.section || item.sheetCodeName || UNGROUPED_SECTION,
        tabNumber: item.tabNumber ?? Number.MAX_SAFE_INTEGER,
        leaves: [],
      };
      groups.set(key, group);
    }
    return group;
  };

  for (const item of items) {
    const group = groupFor(item);
    if (isSheetRow(item)) {
      // The visible tab name first. A Sheet row's name falls back to its
      // Artifact ID when the register leaves Title blank — which it does for
      // every sheet in the real model — so preferring the name showed
      // "wsCover.SHEET" in the upload dialog where every other surface showed
      // "Cover Sheet".
      group.label = item.section || item.name || group.label;
      if (item.tabNumber != null) group.tabNumber = item.tabNumber;
    } else {
      group.leaves.push(item);
    }
  }

  // A sheet with nothing on it has nothing to offer — the Sheet row exists only
  // to label a group, so with no leaves there's no group to show.
  return Array.from(groups.values())
    .filter((group) => group.leaves.length > 0)
    .sort((a, b) => a.tabNumber - b.tabNumber);
};

/** What is still outstanding, for the banner and the warning on Continue.
 *  Exported so the two cannot disagree.
 *
 *  Every artifact comes back with a description now, tables included — a table
 *  is summarised rather than split into rows, and that summary is the only
 *  thing retrieval matches it on. So it is worth a read before Continue for
 *  the same reason a chart's is, and both are counted here.
 *
 *  "working" still counts everything in flight separately, because that is
 *  what Continue has to warn about. */
export const reviewProgress = (artifacts: Artifact[]) => ({
  describedTotal: artifacts.length,
  describedDone: artifacts.filter((a) => !isArtifactEnriching(a) && a.status !== "Error").length,
  working: artifacts.filter((a) => isArtifactEnriching(a)).length,
  failed: artifacts.filter((a) => a.status === "Error").length,
});

/** The project's current upload version: the highest any artefact carries.
 *
 *  Derived rather than stored, because a version is issued only when an upload
 *  changes something — re-uploading an identical workbook consumes no number,
 *  so there is nothing to count.
 *
 *  Take this over every artefact on the project, not just the active ones, or
 *  deactivating the newest would relight an older one as if it were current. */
export const latestUploadVersion = (artifacts: Pick<Artifact, "uploadVersion">[]): number =>
  artifacts.reduce((highest, a) => Math.max(highest, a.uploadVersion ?? 0), 0);

/** Whether the latest upload is the one that added or changed this artefact —
 *  which is what the yellow pill means. */
export const isFromLatestUpload = (
  artifact: Pick<Artifact, "uploadVersion">,
  latest: number
): boolean => latest > 0 && (artifact.uploadVersion ?? 0) === latest;
